import { merge } from 'lodash';
import { calculateForegroundColors, defaultVhost, shadows } from './defaultTheme';
import Color from 'color';
import { colorWithLightness, colorWithOpacity, colorWithoutOpacity } from '../../../common/util/colors';

export const shades = {
	grey0: '#333333',
	grey10: '#3a3b3d',
	grey20: '#6e6e6e',
	grey30: '#98989b',
	grey40: '#c5c5c5',
	grey50: '#e6e6e6',
	grey60: '#f5f5f5',
	grey70: '#ffffff',
};

export const primaryDarkBackgroundColor = '#3f3f42';

export const calculateDarkVhost = (color: string) => {
	const backgroundColor = Color(primaryDarkBackgroundColor);
	let foregroundColor = Color(color);
	while (foregroundColor.contrast(backgroundColor) < 3) {
		foregroundColor = foregroundColor.lighten(0.005);
	}
	return foregroundColor.hex();
};

export const darkVhost = (window as any).vhostObj?.themeColorDark || calculateDarkVhost((window as any).vhostObj?.themecolor || '#3b97a4');

// Dark theme (accessible) colors are calculated with https://webaim.org/resources/contrastchecker
// Don't use dark theme directly in <Root>, it extends and thus requires the default theme as parent.
const darkTheme = (theme) => {
	const vhost = theme?.colors?.vhost === defaultVhost ? darkVhost : theme?.colors?.vhost || darkVhost;
	const colors = {
		vhost,
		green: '#8fc93a',
		yellow: '#EBB25C',
		orange: '#e77335',
		red: '#E37364',
	};
	const vhostShades = {
		vhost10: colorWithLightness(vhost, 0.25),
		vhost20: colorWithLightness(vhost, 0.5),
		vhost30: colorWithLightness(vhost, 0.75),
		vhost40: vhost,
		vhost50: colorWithoutOpacity(colorWithOpacity(vhost, 0.5), '#ffffff'),
		vhost60: colorWithoutOpacity(colorWithOpacity(vhost, 0.2), '#ffffff'),
		vhost70: colorWithoutOpacity(colorWithOpacity(vhost, 0.07), '#ffffff'),
	};

	return calculateForegroundColors(
		merge({}, theme, {
			name: 'dark',
			// For larger text and backgrounds
			colors,
			// For small text
			accessibleColors: {
				...colors,
				green: '#8fc93a',
				yellow: '#fab900',
				orange: '#ED9464',
				red: '#ea968b',
			},
			backgroundColors: {
				primary: '#3f3f42',
				secondary: '#252527',
			},
			textColors: {
				body: shades.grey70,
				light: shades.grey60,
				lighter: shades.grey50,
				lightest: shades.grey40,
			},
			shades,
			vhostShades,
			elevation: {
				z1: {
					backgroundOverlayColor: 'rgba(255, 255, 255, 0.06)',
					backgroundOverlay: 'linear-gradient(rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.06))',
					boxShadow: [...shadows.z1, 'inset 0 0 1px rgba(255, 255, 255, 0.05)', 'inset 0 4px 8px rgba(255, 255, 255, 0.02)'].join(', '),
					dropShadow: shadows.z1.map((shadow) => `drop-shadow(${shadow})`).join(' '),
					insetBoxShadow: ['inset 0 0 1px rgba(255, 255, 255, 0.05)', 'inset 0 4px 8px rgba(255, 255, 255, 0.02)'].join(),
				},
				z2: {
					backgroundOverlayColor: 'rgba(255, 255, 255, 0.07)',
					backgroundOverlay: 'linear-gradient(rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.07))',
					boxShadow: [...shadows.z2, 'inset 0 0 1px rgba(255, 255, 255, 0.075)', 'inset 0 4px 8px rgba(255, 255, 255, 0.02)'].join(', '),
					dropShadow: shadows.z2.map((shadow) => `drop-shadow(${shadow})`).join(' '),
					insetBoxShadow: ['inset 0 0 1px rgba(255, 255, 255, 0.075)', 'inset 0 4px 8px rgba(255, 255, 255, 0.02)'].join(),
				},
				z3: {
					backgroundOverlayColor: 'rgba(255, 255, 255, 0.08)',
					backgroundOverlay: 'linear-gradient(rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.08))',
					boxShadow: [...shadows.z3, 'inset 0 0 1px rgba(255, 255, 255, 0.075)', 'inset 0 4px 8px rgba(255, 255, 255, 0.02)'].join(', '),
					dropShadow: shadows.z3.map((shadow) => `drop-shadow(${shadow})`).join(' '),
					insetBoxShadow: ['inset 0 0 1px rgba(255, 255, 255, 0.075)', 'inset 0 4px 8px rgba(255, 255, 255, 0.02)'].join(),
				},
			},
		})
	);
};

export default darkTheme;
